import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FaExclamationTriangle, FaArrowLeft, FaHome } from "react-icons/fa";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { Helmet } from 'react-helmet-async';

const ErrorPage = ({ statusCode = 404, message, customTitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Set default title and message based on status code if not provided
  const getErrorDetails = () => {
    switch (statusCode) {
      case 400:
        return {
          title: customTitle || "Bad Request",
          message: message || "The request could not be processed due to invalid syntax."
        };
      case 401:
        return {
          title: customTitle || "Unauthorized",
          message: message || "You need to be logged in to access this page."
        };
      case 403:
        return {
          title: customTitle || "Forbidden",
          message: message || "You don't have permission to access this resource."
        };
      case 404:
        return {
          title: customTitle || "Page Not Found",
          message: message || "The page you are looking for doesn't exist or has been moved."
        };
      case 500:
        return {
          title: customTitle || "Server Error",
          message: message || "Something went wrong on our servers. Please try again later."
        };
      default:
        return {
          title: customTitle || "Unexpected Error",
          message: message || "An unexpected error occurred. Please try again later."
        };
    }
  };

  const { title, message: errorMessage } = getErrorDetails();

  // Go back function
  const handleGoBack = () => {
    navigate(-1);
  };

  // Go home function
  const handleGoHome = () => {
    navigate("/");
  };

  // Log the error for debugging purposes
  useEffect(() => {
    console.error(`Error ${statusCode}: ${title} - ${errorMessage}`);
    console.error("Error occurred at path:", location.pathname);
  }, [statusCode, title, errorMessage, location.pathname]);

  return (
    <>
          <Helmet>
        <title>{`${statusCode} – ${title}`}</title>
      </Helmet>
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow flex items-center justify-center py-16 px-4">
        <motion.div 
          className="max-w-3xl w-full bg-white rounded-xl shadow-xl overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Error header with status code */}
          <div className="bg-gradient-to-r from-red-600 to-red-800 p-6 flex items-center">
            <motion.div 
              className="bg-white/20 rounded-full p-4 mr-4"
              initial={{ rotate: 0 }}
              animate={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <FaExclamationTriangle className="text-white text-3xl" />
            </motion.div>
            <div>
              <h1 className="text-white text-2xl font-bold Aleo">{title}</h1>
              <p className="text-white/80 text-lg">Status Code: {statusCode}</p>
            </div>
          </div>
          
          {/* Error content */}
          <div className="p-8">
            <p className="text-gray-700 text-lg mb-8">{errorMessage}</p>
            
            {/* Suggestions */}
            <div className="bg-blue-50 rounded-lg p-6 mb-8 border border-blue-100">
              <h3 className="text-blue-800 font-semibold mb-3">Try these steps:</h3>
              <ul className="list-disc pl-5 space-y-2 text-blue-700">
                <li>Check that the URL was entered correctly</li>
                <li>Go back to the previous page and try again</li>
                <li>Return to the homepage and navigate to your destination</li>
                <li>If the problem persists, please contact our support team</li>
              </ul>
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                onClick={handleGoBack}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium transition-colors"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaArrowLeft />
                <span>Go Back</span>
              </motion.button>
              
              <motion.button
                onClick={handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg font-medium transition-all hover:from-blue-700 hover:to-blue-900"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaHome />
                <span>Return Home</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </main>
      
      <Footer />
    </div>
</>
  );
};

export default ErrorPage;